/**
 * Dashboard Filter Utils - Duplicated structure from My Candidates CandidatesFilterUtils
 * Optimized for dashboard data (Tasks, Cue Notes, Comments)
 */
import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';

export enum DashboardTasksFilterName {
  statusIds = 'statusIds',
  search = 'search',
  dateRange = 'dateRange',
}

export enum DashboardCueNotesFilterName {
  replied = 'replied',
  search = 'search',
  placementId = 'placementId',
  dateRange = 'dateRange',
}

export enum DashboardCommentsFilterName {
  search = 'search',
  placementId = 'placementId',
  taskId = 'taskId',
  rootTaskId = 'rootTaskId',
  entityType = 'entityType',
  dateRange = 'dateRange',
}

// ─── Tasks ─────────────────────────────────────────────────────────────────
const TASK_STATUS_OPTIONS = [
  { id: 0, name: 'All Status (0)', value: '' },
  { id: 1, name: 'New', value: '1' },
  { id: 2, name: 'Pending', value: '2' },
  { id: 3, name: 'In Progress', value: '3' },
  { id: 4, name: 'Completed', value: '4' },
  { id: 5, name: 'Cancelled', value: '5' },
  { id: 6, name: 'On Hold', value: '6' },
];

export const defaultTasksFilterValues = {
  statusIds: null as { id: number; name: string; value: string } | null,
  search: '',
  dateRange: null as { startDate?: string; endDate?: string } | null,
};

export const getDashboardTasksFilterSpecs = (): any[] => [
  {
    filterName: 'Status',
    name: DashboardTasksFilterName.statusIds,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: TASK_STATUS_OPTIONS,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Date Range',
    name: DashboardTasksFilterName.dateRange,
    type: FilterCompTypes.DATERANGEV2,
    variant: 'outlined',
    secondaryLabel: 'Date Range',
    useMaxWidth: true,
    maxWidth: 210,
    version3: true,
    clickAway: true,
    disablePortal: true,
    quickSelect: false,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
];

// ─── Cue Notes ──────────────────────────────────────────────────────────────
const REPLY_STATUS_OPTIONS = (repliedCount: number, pendingCount: number) => [
  { id: 0, name: `All (${repliedCount + pendingCount})`, value: '' },
  { id: 1, name: `Replied (${repliedCount})`, value: 'true' },
  { id: 2, name: `Pending Reply (${pendingCount})`, value: 'false' },
];

export const defaultCueNotesFilterValues = {
  replied: null as { id: number; name: string; value: string } | null,
  search: '',
  placementId: '',
  dateRange: null as { startDate?: string; endDate?: string } | null,
};

export const getDashboardCueNotesFilterSpecs = (
  repliedCount: number,
  pendingCount: number,
): any[] => [
  {
    filterName: 'Search',
    name: DashboardCueNotesFilterName.search,
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Search…',
    applyOnClickAway: false,
  },
  {
    filterName: 'Placement ID',
    name: DashboardCueNotesFilterName.placementId,
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Placement ID',
    applyOnClickAway: false,
  },
  {
    filterName: 'Reply Status',
    name: DashboardCueNotesFilterName.replied,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: REPLY_STATUS_OPTIONS(repliedCount, pendingCount),
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Date Range (Created)',
    name: DashboardCueNotesFilterName.dateRange,
    type: FilterCompTypes.DATERANGEV2,
    variant: 'outlined',
    secondaryLabel: 'Date Range',
    useMaxWidth: true,
    maxWidth: 210,
    version3: true,
    clickAway: true,
    disablePortal: true,
    quickSelect: false,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
];

// ─── Comments ───────────────────────────────────────────────────────────────
const ENTITY_TYPE_OPTIONS = [
  { id: 0, name: 'All', value: '' },
  { id: 1, name: 'External Task', value: 'external_task' },
  { id: 2, name: 'Cue Note', value: 'cue_note' },
];

export const defaultCommentsFilterValues = {
  search: '',
  placementId: '',
  taskId: '',
  rootTaskId: '',
  entityType: null as { id: number; name: string; value: string } | null,
  dateRange: null as { startDate?: string; endDate?: string } | null,
};

export const getDashboardCommentsFilterSpecs = (): any[] => [
  {
    filterName: 'Search',
    name: DashboardCommentsFilterName.search,
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Search…',
    applyOnClickAway: false,
  },
  {
    filterName: 'Placement ID',
    name: DashboardCommentsFilterName.placementId,
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Placement ID',
    applyOnClickAway: false,
  },
  {
    filterName: 'Task ID',
    name: DashboardCommentsFilterName.taskId,
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Task ID',
    applyOnClickAway: false,
  },
  {
    filterName: 'Root Task ID',
    name: DashboardCommentsFilterName.rootTaskId,
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Root Task ID',
    applyOnClickAway: false,
  },
  {
    filterName: 'Entity',
    name: DashboardCommentsFilterName.entityType,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: ENTITY_TYPE_OPTIONS,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Date Range (Created)',
    name: DashboardCommentsFilterName.dateRange,
    type: FilterCompTypes.DATERANGEV2,
    variant: 'outlined',
    secondaryLabel: 'Date Range',
    useMaxWidth: true,
    maxWidth: 210,
    version3: true,
    clickAway: true,
    disablePortal: true,
    quickSelect: false,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
];
